import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api/client'
import type { Draft, DraftSheet } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Spreadsheet } from '../components/Spreadsheet'
import { Notice, fmtTime } from '../components/ui'

const AUTOSAVE_MS = 1200
const MAX_SHEETS = 12

const blankSheet = (n: number): DraftSheet => ({ name: `Sheet${n}`, cells: [] })

/**
 * 草稿表格：敲待入库的域名清单用的草稿纸，自动保存到服务器。
 *
 * 两条刻意的设计：
 *   - 保存是防抖 + 乐观版本。自动保存不能要求 Idempotency-Key（每次都是新意图），
 *     所以并发靠 version 挡；版本不匹配时**不覆盖对方内容**，而是让操作员选留哪份。
 *   - 这里的内容永远不会变成库存或台账：入库仍然只能走「入库」页。页面上明说，
 *     免得有人以为在草稿里敲完域名就已经记上了。
 */
export function Draft() {
  const { user } = useAuth()
  const canWrite = !!user?.canWrite
  const { data, error: loadError, loading, reload } = useAsync(() => api.get<Draft>('/api/draft'), [])

  const [sheets, setSheets] = useState<DraftSheet[]>([blankSheet(1)])
  const [active, setActive] = useState(0)
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'conflict'>('idle')
  const [savedAt, setSavedAt] = useState('')
  const [saveErr, setSaveErr] = useState<unknown>(null)
  const [confirmDrop, setConfirmDrop] = useState(false)
  // 清空没有确认弹窗，所以要在状态栏留一句回显，让人知道刚才那一下发生了什么。
  const [cleared, setCleared] = useState(false)

  const version = useRef(0)
  const timer = useRef<number | undefined>(undefined)
  const clearOp = useOperation<{ cleared: boolean }>('draft-clear')

  useEffect(() => {
    if (!data) return
    version.current = data.version
    setSavedAt(data.updatedAt)
    setSheets(data.sheets?.length ? data.sheets : [blankSheet(1)])
    setActive((a) => Math.min(a, (data.sheets?.length ?? 1) - 1))
    setState('idle')
  }, [data])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const save = useCallback(
    async (next: DraftSheet[]) => {
      setSaveErr(null)
      setState('saving')
      try {
        const res = await api.post<Draft>('/api/draft', { sheets: next, version: version.current })
        version.current = res.version
        setSavedAt(res.updatedAt)
        setState('saved')
      } catch (e) {
        // 别处先保存了一步：停下来等操作员决定，绝不静默覆盖对方的草稿。
        if (e instanceof ApiError && e.code === 'DRAFT_STALE') setState('conflict')
        else {
          setState('idle')
          setSaveErr(e)
        }
      }
    },
    [],
  )

  function edit(next: DraftSheet[]) {
    setSheets(next)
    setCleared(false)
    if (!canWrite) return
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void save(next), AUTOSAVE_MS)
  }

  /** 冲突时选择"用我这份"：先取服务器最新版本号，再带着它重发。 */
  async function keepLocal() {
    try {
      const cur = await api.get<Draft>('/api/draft')
      version.current = cur.version
      await save(sheets)
    } catch (e) {
      setSaveErr(e)
    }
  }

  function addSheet() {
    if (sheets.length >= MAX_SHEETS) return
    const next = [...sheets, blankSheet(sheets.length + 1)]
    setActive(next.length - 1)
    edit(next)
  }

  function renameSheet(name: string) {
    edit(sheets.map((s, i) => (i === active ? { ...s, name } : s)))
  }

  function dropSheet() {
    const next = sheets.filter((_, i) => i !== active)
    setActive(0)
    setConfirmDrop(false)
    edit(next.length ? next : [blankSheet(1)])
  }

  // 点一下就清空：不弹确认、不要打字。草稿是个人草稿纸，误点的代价远小于
  // 每天多按两次按钮；服务器那边照样记一条 DRAFT_CLEAR 审计。
  async function clearAll() {
    const res = await clearOp.run((key) => api.post<{ cleared: boolean }>('/api/draft/clear', {}, key))
    if (!res) return
    version.current = 0
    setSavedAt('')
    setState('idle')
    setSheets([blankSheet(1)])
    setActive(0)
    setCleared(true)
  }

  const sheet = sheets[Math.min(active, sheets.length - 1)] ?? blankSheet(1)
  const statusText = cleared
    ? '已清空全部标签页'
    : state === 'saving'
      ? '正在保存…'
      : state === 'saved'
        ? `已自动保存 ${fmtTime(savedAt)} · 第 ${version.current} 版`
        : ''

  return (
    <section>
      <header className="page-head compact">
        <h1>草稿表格</h1>
        <div className="row">
          <span className="muted" role="status" aria-live="polite">
            {statusText}
          </span>
          {canWrite && (
            <button
              type="button"
              className="danger"
              disabled={clearOp.busy}
              title="一次点击清空所有标签页的内容；已入库的域名不受任何影响"
              onClick={() => void clearAll()}
            >
              {clearOp.busy ? '清空中…' : '清空草稿'}
            </button>
          )}
        </div>
      </header>

      <Notice error={loadError} />
      <Notice error={saveErr} extra={saveErr ? <span className="sub">本次编辑仍在页面上，可以再点一次任意格子触发保存。</span> : undefined} />
      {loading && !data && <p className="muted">载入草稿…</p>}
      {!canWrite && (
        <div className="notice warn">当前账号是只读角色，能查看已保存的草稿，但不能编辑保存。</div>
      )}

      {state === 'conflict' && (
        <div className="notice warn" role="alert">
          <strong>这份草稿在别处也改过了，服务器上没有覆盖你的内容。</strong>
          <p className="sub">选一个：载入服务器那一份，或把你现在这份盖上去（对方的改动会丢）。</p>
          <div className="row">
            <button type="button" onClick={reload}>
              载入服务器版本
            </button>
            <button type="button" className="danger" onClick={() => void keepLocal()}>
              用我这份覆盖
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="sheet-tabs">
          {sheets.map((s, i) => (
            <button
              key={s.name + i}
              type="button"
              className={'sheet-tab' + (i === active ? ' active' : '')}
              onClick={() => setActive(i)}
              aria-pressed={i === active}
            >
              {s.name || `Sheet${i + 1}`}
            </button>
          ))}
          <button type="button" onClick={addSheet} disabled={!canWrite || sheets.length >= MAX_SHEETS}>
            加一个标签页
          </button>
          {sheets.length > 1 && (
            <button type="button" className="danger" disabled={!canWrite} onClick={() => setConfirmDrop(true)}>
              删除当前页
            </button>
          )}
          <label className="sheet-rename">
            <span>当前页名</span>
            <input
              value={sheet.name}
              maxLength={24}
              disabled={!canWrite}
              onChange={(e) => renameSheet(e.target.value)}
              aria-label="当前标签页名称"
            />
          </label>
        </div>

        <Spreadsheet sheet={sheet} disabled={!canWrite} onChange={(next) => edit(sheets.map((s, i) => (i === active ? next : s)))} />

        <p className="sub">
          这里只是草稿纸：内容不会进入库存或台账，要入库请复制到「入库」页。合并单元格 / 条件格式 /
          字体颜色这类排版功能不做 —— 这是清单纸，不是报表。
        </p>
      </div>

      {!!clearOp.error && (
        <Notice error={clearOp.error} extra={<span className="sub">草稿没有被清空，可以再点一次「清空草稿」。</span>} />
      )}

      {confirmDrop && (
        <ConfirmDialog
          title={`删除标签页「${sheet.name}」`}
          description="只删这一个标签页的内容，其它页保持不变。删除后会自动保存。"
          confirmLabel="删除该页"
          busy={false}
          error={null}
          onConfirm={dropSheet}
          onClose={() => setConfirmDrop(false)}
        />
      )}
    </section>
  )
}
